import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { ChevronDown, Phone, RefreshCw, Users, TrendingUp, PhoneCall, PhoneMissed, PhoneOff, ThumbsDown, X, ClipboardList } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

const callStatusOptions = [
  { value: 'warm_lead',     label: 'Interested (W)' },
  { value: 'call_back',     label: 'Call Back' },
  { value: 'not_interested',label: 'Not Interested' },
  { value: 'cold_lead',     label: 'Follow Up (C)' },
  { value: 'not_connected', label: 'Not Connected' },
];

const callStatusLabel = val =>
  callStatusOptions.find(o => o.value === val)?.label || val || '—';

const statusBadgeClass = val => {
  switch (val) {
    case 'warm_lead':      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'call_back':      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'not_interested': return 'bg-red-50 text-red-600 border-red-200';
    case 'cold_lead':      return 'bg-gray-100 text-gray-600 border-gray-300';
    case 'not_connected':  return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    default:               return 'bg-gray-50 text-gray-400 border-gray-200';
  }
};

const getFYDates = () => {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  const fyStartYear = month >= 3 ? year : year - 1;
  return {
    startDate: `${fyStartYear}-04-01`,
    endDate:   `${fyStartYear + 1}-03-31`,
  };
};

const formatDate = d =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// ── Multi-Select ───────────────────────────────────────────────────────────────
function MultiSelect({ options, value = [], onChange, placeholder = 'All' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = v => {
    if (value.includes(v)) onChange(value.filter(x => x !== v));
    else onChange([...value, v]);
  };

  const allSelected = options.length > 0 && value.length === options.length;
  const someSelected = value.length > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) onChange([]);
    else onChange(options.map(o => o.value));
  };

  const label = value.length === 0
    ? placeholder
    : value.length === options.length
      ? 'All selected'
      : value.length === 1
        ? (options.find(o => o.value === value[0])?.label || value[0])
        : `${value.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(p => !p)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-gray-400 transition-colors"
      >
        <span className={value.length ? 'text-gray-800 font-medium truncate' : 'text-gray-400'}>{label}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value.length > 0 && (
            <span
              role="button"
              onClick={e => { e.stopPropagation(); onChange([]); }}
              className="text-gray-300 hover:text-gray-500 cursor-pointer"
            >
              <X size={12} />
            </span>
          )}
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto">
          {/* Select All row */}
          <label className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors border-b border-gray-100 bg-gray-50">
            <input
              type="checkbox"
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleAll}
              className="w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer flex-shrink-0"
            />
            <span className="text-sm font-semibold text-gray-700">Select All</span>
          </label>
          {options.map(o => (
            <label key={o.value} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors">
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer flex-shrink-0"
              />
              <span className="text-sm text-gray-700">{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, colorCls }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border ${colorCls.border} p-4 flex items-center gap-4`}>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${colorCls.bg}`}>
        <Icon size={20} className={colorCls.icon} strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500 truncate">{label}</p>
        <p className={`text-2xl font-bold ${colorCls.text}`}>{value}</p>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function CallerDashboard() {
  const { userInfo } = useSelector(s => s.userLogin || {});
  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  const fy = getFYDates();
  const [startDate, setStartDate]         = useState(fy.startDate);
  const [endDate, setEndDate]             = useState(fy.endDate);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [items, setItems]               = useState([]);
  const [callerUsers, setCallerUsers]   = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [dailyReports, setDailyReports] = useState([]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate)   params.set('endDate', endDate);
      // No userId — fetch all, filter client-side for multi-select

      const [dashRes, dailyRes] = await Promise.all([
        axios.get(`${API}/callers/dashboard?${params.toString()}`, authConfig),
        axios.get(`${API}/daily-call-report/all?${params.toString()}`, authConfig),
      ]);

      setItems(dashRes.data.items || []);
      setDailyReports(dailyRes.data.reports || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // ── Load caller filter options (same logic as CallerList / ProspectList) ──────
  useEffect(() => {
    const myId = (userInfo?.id || userInfo?._id)?.toString();
    const isSuperAdmin = userInfo?.role?.name === 'Super Admin';
    axios.get(`${API}/users`, authConfig)
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];

        if (isSuperAdmin) {
          setCallerUsers(all.map(u => ({ value: u._id?.toString(), label: u.name })));
          return;
        }

        const childrenOf = {};
        all.forEach(u => {
          const parentId = (u.assignedTo?._id || u.assignedTo)?.toString();
          if (parentId) {
            if (!childrenOf[parentId]) childrenOf[parentId] = [];
            childrenOf[parentId].push({ value: u._id?.toString(), label: u.name });
          }
        });

        const result = [];
        const visited = new Set();
        const queue = [myId];
        while (queue.length > 0) {
          const cur = queue.shift();
          for (const child of (childrenOf[cur] || [])) {
            if (!visited.has(child.value)) {
              visited.add(child.value);
              result.push(child);
              queue.push(child.value);
            }
          }
        }

        if (result.length > 0) {
          setCallerUsers(result);
        } else {
          const self = all.find(u => u._id?.toString() === myId);
          setCallerUsers(self ? [{ value: self._id?.toString(), label: self.name }] : []);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Client-side filter by selected callers ──────────────────────────────────
  const filteredItems = selectedUsers.length === 0
    ? items
    : items.filter(r => {
        const id = r.caller?._id || (typeof r.caller === 'string' ? r.caller : '');
        return selectedUsers.includes(id);
      });

  // Filter daily reports by selected callers
  const filteredDailyReports = selectedUsers.length === 0
    ? dailyReports
    : dailyReports.filter(r => {
        const id = r.caller?._id || (typeof r.caller === 'string' ? r.caller : '');
        return selectedUsers.includes(id);
      });

  const totalCallsReported = filteredDailyReports.reduce((sum, r) => sum + (r.totalCalls || 0), 0);

  // Recompute stats from filtered items
  const warm_lead     = filteredItems.filter(r => r.callStatus === 'warm_lead').length;
  const call_back     = filteredItems.filter(r => r.callStatus === 'call_back').length;
  const cold_lead     = filteredItems.filter(r => r.callStatus === 'cold_lead').length;
  const not_connected = filteredItems.filter(r => r.callStatus === 'not_connected').length;

  const stats = {
    warm_lead,
    call_back,
    cold_lead,
    not_connected,
    // Difference: total calls reported minus all other categories
    not_interested: Math.max(0, totalCallsReported - warm_lead - call_back - cold_lead - not_connected),
  };

  const cardDefs = [
    { key: 'warm_lead',      label: 'Interested (W)', icon: TrendingUp,  colorCls: { border: 'border-orange-100', bg: 'bg-orange-50', icon: 'text-orange-600', text: 'text-orange-700' } },
    { key: 'call_back',      label: 'Call Back',      icon: PhoneCall,   colorCls: { border: 'border-indigo-100', bg: 'bg-indigo-50', icon: 'text-indigo-600', text: 'text-indigo-700' } },
    { key: 'not_interested', label: 'Not Interested', icon: ThumbsDown,  colorCls: { border: 'border-red-100',    bg: 'bg-red-50',    icon: 'text-red-500',    text: 'text-red-600'    } },
    { key: 'cold_lead',      label: 'Follow Up (C)',  icon: PhoneMissed, colorCls: { border: 'border-gray-200',   bg: 'bg-gray-100',  icon: 'text-gray-500',   text: 'text-gray-700'   } },
    { key: 'not_connected',  label: 'Not Connected',  icon: PhoneOff,    colorCls: { border: 'border-yellow-100', bg: 'bg-yellow-50', icon: 'text-yellow-600', text: 'text-yellow-700' } },
  ];

  const callerOptions = callerUsers;

  // ── Per-caller breakdown ────────────────────────────────────────────────────
  const callerStatsMap = {};
  filteredItems.forEach(r => {
    const id   = r.caller?._id || (typeof r.caller === 'string' ? r.caller : '');
    const name = r.caller?.name || r.callerName || '—';
    if (!callerStatsMap[id]) callerStatsMap[id] = { name, warm_lead: 0, call_back: 0, cold_lead: 0, not_connected: 0, totalCalls: 0 };
    const s = r.callStatus;
    if (s && s in callerStatsMap[id]) callerStatsMap[id][s]++;
  });
  filteredDailyReports.forEach(r => {
    const id   = r.caller?._id || (typeof r.caller === 'string' ? r.caller : '');
    const name = r.caller?.name || '—';
    if (!callerStatsMap[id]) callerStatsMap[id] = { name, warm_lead: 0, call_back: 0, cold_lead: 0, not_connected: 0, totalCalls: 0 };
    callerStatsMap[id].totalCalls += r.totalCalls || 0;
  });
  const callerStatsRows = Object.entries(callerStatsMap).map(([id, d]) => ({
    id, name: d.name,
    warm_lead:     d.warm_lead,
    call_back:     d.call_back,
    cold_lead:     d.cold_lead,
    not_connected: d.not_connected,
    not_interested: Math.max(0, d.totalCalls - d.warm_lead - d.call_back - d.cold_lead - d.not_connected),
    totalCalls:    d.totalCalls,
  }));

  return (
    <div className="px-2 py-3 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Caller Dashboard</h1>
        <button
          onClick={fetchDashboard}
          disabled={loading}
          className="inline-flex items-center gap-1.5 border border-gray-300 hover:bg-white text-gray-600 text-sm font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              <span className="inline-flex items-center gap-1"><Users size={12} /> Caller</span>
            </label>
            <MultiSelect
              options={callerOptions}
              value={selectedUsers}
              onChange={setSelectedUsers}
              placeholder="All Callers"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={() => {
              const f = getFYDates();
              setStartDate(f.startDate);
              setEndDate(f.endDate);
              setSelectedUsers([]);
            }}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium underline underline-offset-2"
          >
            Reset to current FY
          </button>
          {selectedUsers.length > 0 && (
            <span className="text-xs text-gray-500">
              {filteredItems.length} of {items.length} records
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-5">
          {error}
        </div>
      )}

      {/* Daily Call Report Summary Card */}
      <div className="mb-4">
        <StatCard
          label="Total Calls Reported (Daily Form)"
          value={loading ? '—' : totalCallsReported}
          icon={ClipboardList}
          colorCls={{ border: 'border-teal-100', bg: 'bg-teal-50', icon: 'text-teal-600', text: 'text-teal-700' }}
        />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {cardDefs.map(card => (
          <StatCard
            key={card.key}
            label={card.label}
            value={loading ? '—' : stats[card.key]}
            icon={card.icon}
            colorCls={card.colorCls}
          />
        ))}
      </div>

      {/* User-wise Breakdown Table */}
      {!loading && callerStatsRows.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Caller-wise Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr>
                  {['Caller', 'Total Calls', 'Interested (W)', 'Call Back', 'Not Interested', 'Follow Up (C)', 'Not Connected'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {callerStatsRows.map(row => (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.name}</td>
                    <td className="px-4 py-3 text-sm text-teal-700 font-semibold">{row.totalCalls}</td>
                    <td className="px-4 py-3 text-sm text-orange-600 font-semibold">{row.warm_lead}</td>
                    <td className="px-4 py-3 text-sm text-indigo-600 font-semibold">{row.call_back}</td>
                    <td className="px-4 py-3 text-sm text-red-600 font-semibold">{row.not_interested}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-semibold">{row.cold_lead}</td>
                    <td className="px-4 py-3 text-sm text-yellow-600 font-semibold">{row.not_connected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">Records</h2>
          {!loading && <span className="text-xs text-gray-400">{filteredItems.length} entr{filteredItems.length === 1 ? 'y' : 'ies'}</span>}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-12">No records found for the selected filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr>
                  {['S.No', 'Caller', 'Case Name', 'Mobile', 'Loan Type', 'Status', 'Date'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map((row, idx) => (
                  <tr key={row._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.caller?.name || row.callerName || '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.caseName || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.mobileNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.loanType || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {row.callStatus ? (
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${statusBadgeClass(row.callStatus)}`}>
                          {callStatusLabel(row.callStatus)}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
